import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { FiscalDocumentInformation } from "@/features/privateCompanies/components/showBranch/fiscalDocumentInformation";
import { BranchInformation } from "@/features/privateCompanies/components/showBranch/branchInformation"
import { ConsumptionRestriction } from "@/features/privateCompanies/components/showBranch/consumptionRestriction";
import { CreditConfiguration } from "@/features/privateCompanies/components/showBranch/creditConfiguration";
import { EstablishmentRestriction } from "@/features/privateCompanies/components/showBranch/establishmentRestriction";
import { GeneralInformation } from "@/features/privateCompanies/components/showBranch/generalInformation"
import { MasterClientUser } from "@/features/privateCompanies/components/showBranch/masterClientUser"
import { PeriodRestriction } from "@/features/privateCompanies/components/showBranch/periodRestriction"
import { useCompany } from "@/features/privateCompanies/hooks/useCompany"
import { useCompanyBranch } from "@/features/privateCompanies/hooks/useCompanyBranch"
import { BriefcaseBusinessIcon } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import usePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLead";
import { usePublicCompanyLead } from "@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLead";
import CardConfiguration from "@/features/privateCompanies/components/showBranch/cardConfiguration";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import BlockUnblockCompanyBranch from "../components/BlockUnblockCompanyBranch";
import AdditionalDocuments from "@/features/privateCompanies/components/showBranch/additionalDocuments";
import { ShowHeader } from "@/common/components/ShowHeader";
import CompanyBranchUserAccesses from "../components/CompanyBranchUserAccesses";
import Tabs from "@/common/components/Tabs";
import { useCurrentTabInfo } from "@/common/hooks/useCurrentTabInfo";

const Show = () => {
  const { companyId, id: branchId } = useParams()
  const navigate = useNavigate()

  const {
    data: companyData,
    isPending: companyDataIsPending,
  } = useCompany(companyId || '')

  const {
    data: branchData,
    isFetching: branchDataIsFetching,
    isPending: branchDataIsPending,
    error: branchDataError = {} as any,
    refetch: refetchBranchData,
    dataUpdatedAt: companyBranchUpdatedAt,
  } = useCompanyBranch(branchId || '')

  const { data: privateCompanyLead } = usePrivateCompanyLead(
    companyData?.company_lead_id || '',
    companyData?.kind === 'private_company' && !!companyData?.company_lead_id
  )

  const { data: publicCompanyLead } = usePublicCompanyLead(
    companyData?.company_lead_id || '',
    companyData?.kind === 'public_company' && !!companyData?.company_lead_id
  )

  const { currentTab } = useCurrentTabInfo([
    { value: '' },
    { value: 'user-branch-access' },
  ]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={branchDataError?.status}
      refetchFunction={refetchBranchData}
      notFoundFallback={`/companies/${companyId}/branches`}
      notFoundButtonText="Ir para Estrutura"
    >
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/companies/${companyId}/branches`,
          searchLastRouteFromReference: `/companies/${companyId}/branches/${branchId}`,
        }}
        isLoading={companyDataIsPending || branchDataIsPending}
        LogoPlaceholder={(
          <ImageWithPlaceholder
            className="size-8"
            iconClassName="size-6"
            placeholderIcon={BriefcaseBusinessIcon}
            url={companyData?.general_information?.company_logo?.url}
            alt={companyData?.general_information?.fantasy_name}
          />
        )}
        titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-md"
        subtitleClassName="text-nowrap font-medium"
        title={companyData?.general_information.fantasy_name}
        subtitle={branchData?.display_name}
        buttons={!currentTab && (
          <Button
            type="button"
            name="update"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            operationKlassToCheckPermission="Admin::Client::CompanyBranches::Update"
            onClick={() => navigate(`/companies/${companyId}/branches/${branchId}/edit`)}
          >
            Editar
          </Button>
        ) || undefined}
      />

      <Tabs
        tabsListClassName="w-full bg-transparent rounded-none"
        tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
        tabsContentClassName="mt-6"
        isLoading={companyDataIsPending}
        skeletonsCount={2}
        tabsList={[
          {
            value: '',
            label: 'Dados gerais',
            body: (
              <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-200px)]">
                {branchData?.id !== branchData?.master_client_user?.branch.id && (
                  <BlockUnblockCompanyBranch companyBranch={branchData} />
                )}

                <p className="text-lg font-bold">Dados da filial</p>
                <BranchInformation
                  displayName={branchData?.display_name}
                  parentId={branchData?.parent_id}
                  parentDisplayName={branchData?.parent_display_name}
                  shouldHaveOwnBillWhenDynamic={branchData?.should_have_own_bill_when_dynamic}
                  billBy={
                    companyData?.kind === 'private_company'
                      ? privateCompanyLead?.contract?.bill_by
                      : publicCompanyLead?.contract?.bill_by
                  }
                />

                <GeneralInformation
                  companyId={companyId}
                  parentId={branchData?.parent_id}
                  generalInformation={branchData?.general_information}
                  branchDataIsFetching={branchDataIsFetching}
                />

                <CardConfiguration branchData={branchData} />

                <MasterClientUser masterClientUser={branchData?.master_client_user} />

                <CreditConfiguration
                  branchData={branchData}
                  companyData={companyData}
                  controlledBy={
                    companyData?.kind === 'private_company'
                      ? privateCompanyLead?.contract?.consumption_controlled_by
                      : publicCompanyLead?.contract?.consumption_controlled_by
                  }
                  contractProductCategories={
                    companyData?.kind === 'public_company'
                      ? publicCompanyLead?.contract?.contract_product_categories
                      : undefined
                  }
                />

                {Number(branchData?.restriction.allowed_or_blocked_establishments?.length) > 0 && (
                  <EstablishmentRestriction restriction={branchData?.restriction} />
                )}

                {(
                  Number(branchData?.restriction.minimum_kilometers_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.maximum_kilometers_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.minimum_seconds_interval_between_consumptions) > 0 ||
                  Number(branchData?.restriction.minimum_seconds_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.maximum_seconds_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.product_category_restrictions?.length) > 0
                ) && (
                    <ConsumptionRestriction
                      restriction={branchData?.restriction}
                    />
                  )}

                {Number(branchData?.restriction.period_restrictions?.length) > 0 && (
                  <PeriodRestriction periodRestrictions={branchData?.restriction.period_restrictions} />
                )}

                {(!!branchData?.fiscal_document_information?.fiscal_document_kind ||
                  !!branchData?.fiscal_document_information?.consumption_observations ||
                  !!branchData?.fiscal_document_information?.service_observations) && (
                    <FiscalDocumentInformation fiscal_document_information={branchData?.fiscal_document_information} />
                  )}

                <AdditionalDocuments
                  branchId={branchId}
                  additionalDocuments={branchData?.additional_documents || []}
                  companyBranchUpdatedAt={companyBranchUpdatedAt}
                />
              </Box>
            ),
          },
          {
            value: 'user-branch-access',
            label: 'Permissão de usuários',
            hidden: companyData?.branch_hierarchy_for_user_access !== 'dynamic',
            operationKlassToCheckPermission: 'Admin::Client::UserBranchAccesses::Filter',
            body: <CompanyBranchUserAccesses companyBranch={branchData} />
          },
        ]}
      />
    </ErrorMessageWrapper>
  )
}

export default Show
